import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";

export interface WheelPickerProps {
  options: Array<{ label: string; value: string }>;
  selectedValue: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  height?: number;
  itemHeight?: number;
}

const WheelPicker: React.FC<WheelPickerProps> = ({
  options,
  selectedValue,
  onValueChange,
  placeholder,
  className,
  height = 200,
  itemHeight = 40,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const unlockProgrammaticRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const scrollEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const paddingTop = useMemo(() => itemHeight * 2, [itemHeight]);
  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === selectedValue),
    [options, selectedValue],
  );
  const showPlaceholder = Boolean(placeholder) && !selectedValue;

  const getScrollTopForIndex = useCallback(
    (targetIndex: number, containerHeight: number) =>
      targetIndex * itemHeight +
      paddingTop -
      containerHeight / 2 +
      itemHeight / 2,
    [itemHeight, paddingTop],
  );

  const getClampedIndexFromScroll = useCallback(
    (container: HTMLDivElement) => {
      if (options.length === 0) return -1;
      const centerScrollPosition =
        container.scrollTop + container.clientHeight / 2 - paddingTop;
      const targetIndex = Math.round(centerScrollPosition / itemHeight);
      return Math.max(0, Math.min(options.length - 1, targetIndex));
    },
    [itemHeight, options.length, paddingTop],
  );

  const snapToCenter = useCallback(
    (targetIndex: number, behavior: ScrollBehavior = "smooth") => {
      const container = scrollRef.current;
      if (!container || targetIndex < 0 || targetIndex >= options.length) {
        return;
      }

      const targetScrollTop = getScrollTopForIndex(
        targetIndex,
        container.clientHeight,
      );

      if (Math.abs(container.scrollTop - targetScrollTop) < 1) {
        return;
      }

      isProgrammaticScrollRef.current = true;
      if (unlockProgrammaticRef.current) {
        clearTimeout(unlockProgrammaticRef.current);
      }

      container.scrollTo({
        top: targetScrollTop,
        behavior,
      });

      unlockProgrammaticRef.current = setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 220);
    },
    [getScrollTopForIndex, options.length],
  );

  const commitSelectionFromScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || options.length === 0) return;

    const clampedIndex = getClampedIndexFromScroll(container);
    if (clampedIndex < 0) return;

    const option = options[clampedIndex];
    if (option && option.value !== selectedValue) {
      onValueChange(option.value);
    }

    snapToCenter(clampedIndex);
  }, [
    getClampedIndexFromScroll,
    onValueChange,
    options,
    selectedValue,
    snapToCenter,
  ]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isProgrammaticScrollRef.current) return;

    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    scrollEndTimeoutRef.current = setTimeout(() => {
      commitSelectionFromScroll();
    }, 120);
  }, [commitSelectionFromScroll]);

  const optionNodes = useMemo(
    () =>
      options.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <div
            key={option.value}
            className={cn(
              "mebi-body-small-semibold flex flex-shrink-0 items-center justify-center rounded-xl px-2 transition-all duration-150",
              isSelected
                ? "text-Color-Text-brand-default"
                : "text-Color-Text-neutral-black/75",
            )}
            style={{
              height: `${itemHeight}px`,
              willChange: "color",
            }}
          >
            {option.label}
          </div>
        );
      }),
    [options, selectedValue, itemHeight],
  );

  useEffect(() => {
    if (scrollRef.current && selectedIndex >= 0) {
      snapToCenter(selectedIndex, "auto");
    }
  }, [selectedIndex, snapToCenter]);

  useEffect(() => {
    return () => {
      if (unlockProgrammaticRef.current) {
        clearTimeout(unlockProgrammaticRef.current);
      }
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div
        className="pointer-events-none absolute left-2 right-2 z-10 rounded-xl border border-Color-Stroke-brand bg-Color-Background-brand-inverted/60 shadow-[0_6px_16px_-10px_rgba(0,0,0,0.5)]"
        style={{
          top: `calc(50% - ${itemHeight / 2}px)`,
          height: `${itemHeight}px`,
        }}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-9 rounded-t-2xl bg-gradient-to-b from-Color-Background-neutral-default_secondary via-Color-Background-neutral-default_secondary/75 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-9 rounded-b-2xl bg-gradient-to-t from-Color-Background-neutral-default_secondary via-Color-Background-neutral-default_secondary/75 to-transparent" />

      <div
        ref={scrollRef}
        className="wheel-picker-scroll flex flex-col overflow-y-auto rounded-2xl border border-Color-Stroke-neutral bg-Color-Background-neutral-default_secondary/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]"
        style={{
          height: `${height}px`,
          padding: `${paddingTop}px 12px`,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          willChange: "scroll-position",
        }}
        onScroll={handleScroll}
      >
        <style>
          {`
              .wheel-picker-scroll::-webkit-scrollbar { 
                display: none; 
              }
            `}
        </style>

        {showPlaceholder ? (
          <div
            className="mebi-body-small-semibold flex flex-shrink-0 items-center justify-center text-Color-Text-neutral-supplement"
            style={{
              height: `${itemHeight}px`,
            }}
          >
            {placeholder}
          </div>
        ) : (
          optionNodes
        )}
      </div>
    </div>
  );
};

WheelPicker.displayName = "WheelPicker";

export { WheelPicker };
